import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import Checkbox from '@repo/ui/checkbox'
import { XIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Input from '@repo/ui/input'
import Loader from '@repo/ui/loader'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { TariffAdjustmentTypeEnum } from '../../../../api/enrollments/create-tariff-adjustment'
import {
  getApplicationFees,
  getFamilyDiscount
} from '../../../../api/enrollments/get-application-fees'
import { getEnrollmentPropositionPromoCodeApplication } from '../../../../api/enrollments/get-enrollment-proposition-promocode-application'
import {
  EnrollmentPropositionSummaryType,
  getEnrollmentPropositionPricing,
  getEnrollmentPropositionPricingById
} from '../../../../api/enrollments/get-enrollment-proposition-summary'
import { checkPromoCode } from '../../../../api/promo-codes/check-promocode'
import { useObserver } from '../../../../lib/contexts/observer'
import { ApplicationStatusEnum } from '../../../../lib/types/enums/enrollment/application-status.enum'
import { PromoCodeApplicationEnum } from '../../../../lib/types/enums/promo-code/promo-code-application.enum'
import { PromoCodeTypeEnum } from '../../../../lib/types/enums/promo-code/promo-code-type.enum'
import { EnrollmentPropositionLight } from '../../../../lib/types/models/enrollment/enrollment-light'
import { EnrollmentPropositionInput } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition-input'
import type { PromoCode } from '../../../../lib/types/models/promo-code/promo-code'
import {
  extractAllErrorMessages,
  showAxiosError
} from '../../../../lib/utils/functions/misc.functions'
import PriceLabel from '../../../elements/price-label'
import { mapEnrollmentPropositionInputToRequest } from '../utils/enrollments-utils'

type Props = {
  title?: React.ReactNode
  className?: string
  readonly?: boolean
  enrollmentId?: number
  propositionId?: number
  status?: ApplicationStatusEnum
  input?: EnrollmentPropositionInput
  mainProposition?: EnrollmentPropositionLight
  isClearPromoCodePending?: boolean
  onInputChange: (input: EnrollmentPropositionInput) => Promise<void>
  onClearPromoCode?: () => void
}

type PromocodeState = {
  status: 'none' | 'success' | 'error'
  value: string
  error?: string
}

type CheckedPromoCode = Pick<PromoCode, 'code' | 'discount' | 'includedInsurance' | 'type'>

export const EnrollmentPropositionSummarySkeleton = () => {
  return (
    <InnerBox
      className={'drop-shadow-lg bg-white rounded-lg px-5'}
      titleColor={tailwindColors.black.DEFAULT}
    >
      <Skeleton className={'h-6 w-full max-w-48 rounded-xs'} />

      <div className={'flex flex-col gap-3 h-full my-6 justify-between'}>
        <div className={'flex flex-col gap-3 mb-3'}>
          {Array.from({ length: 9 }).map((_, index) => (
            <Skeleton key={index} className={'h-4 w-full rounded-xs'} />
          ))}
        </div>

        <div className={'flex flex-col gap-3'}>
          <div className={'h-1 w-full border-t border-dashed border-grey-light-hover'} />

          <Skeleton className={'h-4 w-full rounded-xs'} />
          <Skeleton className={'h-4 w-full rounded-xs'} />
        </div>
      </div>

      <Skeleton className={'h-10 w-full rounded-xs'} />
    </InnerBox>
  )
}

const EnrollmentPropositionSummary = ({
  title,
  className,
  readonly,
  mainProposition,
  input,
  isClearPromoCodePending,
  enrollmentId,
  status,
  propositionId,
  onInputChange,
  onClearPromoCode
}: Props) => {
  const [data, setData] = useState<EnrollmentPropositionSummaryType>()
  const [checkedPromoCode, setCheckedPromoCode] = useState<CheckedPromoCode | null>(null)

  const [loading, setLoading] = useState({
    applicationFees: false,
    familyDiscount: false,
    total: false,
    page: true
  })

  const { data: applicationFees } = useQuery({
    queryKey: ['enrollments-application-fees'],
    queryFn: getApplicationFees
  })

  const { data: familyDiscount } = useQuery({
    queryKey: ['enrollments-family-discount'],
    queryFn: getFamilyDiscount
  })

  const { data: promoCodeApplication, refetch: refetchPromoCodeApplication } = useQuery({
    queryKey: ['enrollment-proposition-promocode-application', enrollmentId, propositionId],
    queryFn: () =>
      getEnrollmentPropositionPromoCodeApplication({
        enrollmentId: enrollmentId!,
        propositionId: propositionId!
      }),
    enabled: !!enrollmentId && !!propositionId
  })

  const { refetch } = useQuery({
    queryKey: ['enrollmentProposition', { ...input, enrollmentId, propositionId }],
    queryFn: async () => {
      setLoading((prev) => ({ ...prev, total: true }))
      let res: EnrollmentPropositionSummaryType | undefined

      // Prioritize ID-based fetching when IDs are available
      if (enrollmentId && propositionId) {
        res = await getEnrollmentPropositionPricingById({ enrollmentId, propositionId })
      } else if (input) {
        res = await getEnrollmentPropositionPricing({
          ...mapEnrollmentPropositionInputToRequest(input),
          status
        })
      }

      if (res) {
        setData(res)
        setLoading((prev) => ({ ...prev, total: false, page: false }))
        return res
      }
    }
  })

  useObserver({
    key: 'enrollment-proposition-summary',
    update: async () => {
      await Promise.all([
        refetch(),
        enrollmentId && propositionId ? refetchPromoCodeApplication() : Promise.resolve()
      ])
    }
  })

  useEffect(() => {
    if (enrollmentId && propositionId) refetchPromoCodeApplication()
  }, [enrollmentId, propositionId, refetchPromoCodeApplication])

  useEffect(() => {
    if (!input?.promoCode) setCheckedPromoCode(null)
  }, [input?.promoCode])

  const differencePrice = (data?.total ?? 0) - (mainProposition?.totalPriceAfterDiscount ?? 0)
  const includeApplicationFees =
    ((input?.pricing?.includeApplicationFees ??
      (data?.applicationFee && data.applicationFee > 0)) &&
      readonly) ||
    !readonly

  const includeFamilyDiscount =
    ((input?.pricing?.includeFamilyDiscount ??
      (data?.familyDiscount && data.familyDiscount > 0)) &&
      readonly) ||
    !readonly

  const effectivePromoCodeApplication = data?.promoCode ? promoCodeApplication : undefined
  const hasAutomaticPromoCode =
    effectivePromoCodeApplication === PromoCodeApplicationEnum.AUTOMATIC
  const hasCse = !!data?.cse || !!input?.cseCode
  const fallbackPromoCodeDiscount =
    data && input?.promoCode && checkedPromoCode?.code === input.promoCode
      ? (() => {
          const insuranceTotal = data.insurances.reduce(
            (acc, insurance) => acc + insurance.price,
            0
          )
          const discountableAmount = checkedPromoCode.includedInsurance
            ? data.totalBeforeDiscount
            : data.totalBeforeDiscount - insuranceTotal

          return checkedPromoCode.type === PromoCodeTypeEnum.PERCENTAGE
            ? (discountableAmount * checkedPromoCode.discount) / 100
            : checkedPromoCode.discount
        })()
      : undefined
  const promoCodeDiscount = input?.promoCode
    ? (data?.promoCode?.discount ?? fallbackPromoCodeDiscount)
    : undefined

  if (!data || loading.page) return <EnrollmentPropositionSummarySkeleton />

  return (
    <InnerBox
      className={cn('drop-shadow-lg bg-white rounded-lg p-5', className)}
      title={!title ? 'Détail du tarif (tout inclus)' : undefined}
      titleColor={tailwindColors.black.DEFAULT}
    >
      {title}

      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-col gap-3">
          <PriceLabel
            pricesInfo={{
              label: 'Frais de séjour (thème inclus)',
              price: data.accomodationCosts,
              isBadge: false
            }}
          />

          <PriceLabel
            title="Option(s) : "
            emptyMessage="Aucune option sélectionnée"
            pricesInfo={data.options.map((option) => ({
              label: option.title,
              price: option.price,
              isBadge: true
            }))}
          />

          {includeApplicationFees && (
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                {!readonly && input && (
                  <Checkbox
                    disabled={hasCse}
                    loading={loading.applicationFees}
                    checked={hasCse ? true : input.pricing?.includeApplicationFees}
                    onChange={() => {
                      setLoading((prev) => ({ ...prev, applicationFees: true }))
                      onInputChange({
                        ...input,
                        pricing: {
                          ...input.pricing,
                          includeApplicationFees: !input.pricing?.includeApplicationFees
                        }
                      }).then(() => setLoading((prev) => ({ ...prev, applicationFees: false })))
                    }}
                  />
                )}
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Frais de dossier
                </Typography>
              </div>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {applicationFees}€
              </Typography>
            </div>
          )}

          <PriceLabel
            title="Frais de voyage : "
            variant={'primary'}
            textColor={tailwindColors.primary.DEFAULT}
            pricesInfo={[
              { label: 'Voyage principal', price: data.mainTrip, isBadge: true },
              ...((data.secondaryTrip &&
                data.secondaryTrip > 0 && [
                  { label: 'Voyage secondaire', price: data.secondaryTrip, isBadge: true }
                ]) ||
                [])
            ]}
          />

          <PriceLabel
            title="Assurance : "
            emptyMessage="Aucune assurance sélectionnée"
            pricesInfo={data.insurances.map((insurance) => ({
              label: insurance.name,
              price: insurance.price,
              isBadge: true
            }))}
          />

          {!hasCse && promoCodeDiscount !== undefined && (
            <PriceLabel
              pricesInfo={{
                label: 'Code avantage',
                price: promoCodeDiscount,
                isBadge: false,
                sign: '-'
              }}
            />
          )}

          {data.cse && (
            <PriceLabel
              pricesInfo={{
                label: 'Avantage CSE',
                price: data.cse.discount,
                isBadge: false,
                sign: '-'
              }}
            />
          )}

          <div className="h-1 w-full border-t border-dashed border-grey-light-hover" />

          {!hasCse && includeFamilyDiscount && (
            <div className="flex items-center justify-between">
              <div className="flex gap-2 items-center">
                {!readonly && input && (
                  <Checkbox
                    loading={loading.familyDiscount}
                    checked={input.pricing?.includeFamilyDiscount}
                    onChange={() => {
                      setLoading((prev) => ({ ...prev, familyDiscount: true }))
                      onInputChange({
                        ...input,
                        pricing: {
                          ...input.pricing,
                          includeFamilyDiscount: !input.pricing?.includeFamilyDiscount
                        }
                      }).finally(() => setLoading((prev) => ({ ...prev, familyDiscount: false })))
                    }}
                  />
                )}
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Réduction famille
                </Typography>
              </div>
              <Typography variant="label-semibold" color={tailwindColors.black.DEFAULT}>
                {familyDiscount}€
              </Typography>
            </div>
          )}

          {!!data.tariffAdjustments?.length && (
            <>
              <div>
                <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                  Ajustement :
                </Typography>
                {data.tariffAdjustments.map((ta, index) => (
                  <PriceLabel
                    key={index}
                    pricesInfo={{
                      isBadge: true,
                      label: ta.reason || 'Ajustement de tarif',
                      price: ta.amount,
                      sign: ta.type === TariffAdjustmentTypeEnum.DISCOUNT ? '-' : '+'
                    }}
                  />
                ))}
              </div>

              <div className="h-1 w-full border-t border-dashed border-grey-light-hover" />
            </>
          )}

          <div className="flex items-center justify-between">
            <Typography variant="label-semibold" color={tailwindColors.secondary.DEFAULT}>
              Prix total
            </Typography>

            <Badge loading={loading.total} variant={'warning'} text={data.total + '€'} />
          </div>

          {mainProposition?.totalPriceAfterDiscount !== undefined && differencePrice !== 0 && (
            <div className="flex items-center justify-between">
              <Typography variant="label-semibold" color={tailwindColors.blue.DEFAULT}>
                Différence de prix
                <br />
                (par rapport au séjour sélectionné)
              </Typography>

              <Badge
                variant={'blue'}
                loading={loading.total}
                text={
                  (differencePrice > 0
                    ? `+${differencePrice.toFixed(2)}`
                    : differencePrice.toFixed(2)) + '€'
                }
              />
            </div>
          )}
          <div />
        </div>

        {!readonly && !hasAutomaticPromoCode && input && !hasCse && (
          <EnrollmentPropositionSummary.PromoCode
            input={input}
            onInputChange={onInputChange}
            onClearPromoCode={onClearPromoCode}
            isClearPromoCodePending={isClearPromoCodePending}
            onPromoCodeChecked={setCheckedPromoCode}
            status={status}
          />
        )}
      </div>
    </InnerBox>
  )
}

export default EnrollmentPropositionSummary

type EnrollmentPropositionSummaryPromoCodeProps = {
  input: EnrollmentPropositionInput
  isClearPromoCodePending?: boolean
  onInputChange: (input: EnrollmentPropositionInput) => void
  onClearPromoCode?: () => void
  onPromoCodeChecked?: (promoCode: CheckedPromoCode | null) => void
  status?: ApplicationStatusEnum
  // where the screen takes a CSE code, shown after a refused code
  cseCodeHint?: string
}

EnrollmentPropositionSummary.PromoCode = ({
  input,
  isClearPromoCodePending,
  onInputChange,
  onClearPromoCode,
  onPromoCodeChecked,
  status,
  cseCodeHint = 'Pour un code CSE, utilisez « Ajouter un code »'
}: EnrollmentPropositionSummaryPromoCodeProps) => {
  const [promocode, setPromocode] = useState<PromocodeState>({
    status: 'none',
    value: input.promoCode ?? ''
  })
  // the selection is priced with the code before it is applied: the backend refuses a code that
  // would make the total negative, and its message is shown while the code stays unapplied
  const { mutate: applyPromoCodeMutation, isPending: isApplyPromoCodePending } = useMutation({
    mutationFn: (res: PromoCode) =>
      getEnrollmentPropositionPricing({
        ...mapEnrollmentPropositionInputToRequest(input),
        promoCode: res.code,
        status
      }).then(() => res),
    onSuccess: (res) => {
      setPromocode({ status: 'success', value: res.code })
      onPromoCodeChecked?.(res)
      toast.success('Code avantage appliqué')
      onInputChange({ ...input, promoCode: res.code })
    },
    onError: (error) => {
      showAxiosError(error)
      setPromocode({ status: 'none', value: promocode.value })
    }
  })

  const { mutate, isPending: isCheckPromocodePending } = useMutation({
    mutationFn: checkPromoCode,
    onSuccess: (res) => applyPromoCodeMutation(res),
    // the backend says why the code is refused; a CSE code is not a promo code, it has its own entry
    onError: (error) => {
      setPromocode({
        status: 'error',
        value: promocode.value,
        error: `${extractAllErrorMessages(error).join('. ')}. ${cseCodeHint}`
      })
      onPromoCodeChecked?.(null)
      onInputChange({ ...input, promoCode: null })
    }
  })

  // follow the applied code only: a refused code stays in the field with its error message
  useEffect(() => {
    if (input.promoCode && input.promoCode !== promocode.value)
      setPromocode({ status: 'success', value: input.promoCode })
    else if (!input.promoCode && promocode.status !== 'error')
      setPromocode({ status: 'none', value: '' })
  }, [input.promoCode])

  if (input.cseCode) return null

  return (
    <div className="p-3 flex flex-col gap-1 shadow rounded">
      <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
        Appliquer un code avantage
      </Typography>
      <div className="h-1" />
      <Input
        type="text"
        errorMessage={promocode.error}
        label="Code avantage"
        value={promocode.value}
        wrapperClassName={cn({ 'border-primary': promocode.status === 'success' })}
        onChange={(e) => {
          setPromocode({ status: 'none', value: e.target.value })
        }}
        suffix={
          <div className="flex items-center gap-2">
            {promocode.value && (
              <button
                type="button"
                className="bg-white-active rounded-sm w-5 h-5 flex items-center justify-center"
                onClick={() => {
                  setPromocode({ status: 'none', value: '' })
                  onPromoCodeChecked?.(null)
                  onClearPromoCode?.()
                }}
              >
                {isClearPromoCodePending ? (
                  <Loader />
                ) : (
                  <XIcon width="15" height="15" color={tailwindColors.grey.lighter} />
                )}
              </button>
            )}
            <Button
              label="Appliquer"
              variant="link"
              type="button"
              loading={isCheckPromocodePending || isApplyPromoCodePending}
              disabled={!promocode.value}
              onClick={() => {
                if (input.journey?.id) {
                  mutate({ code: promocode.value, journeyId: input.journey?.id })
                }
              }}
            />
          </div>
        }
      />
    </div>
  )
}
